import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, effect, inject, input, output, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { MobileVoiceCaptureService } from "../../platform/mobile-voice-capture.service";

export type MobileVoicePurchaseRequestRecorderPhase = "idle" | "recording" | "finished";

@Component({
  selector: "mobile-voice-purchase-request-recorder",
  imports: [CommonModule, FormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-voice-purchase-request-recorder.component.html",
  styleUrl: "./mobile-voice-purchase-request-recorder.component.scss",
})
export class MobileVoicePurchaseRequestRecorderComponent implements OnDestroy {
  private readonly voiceCapture = inject(MobileVoiceCaptureService);

  /** Libellé contexte (ex. nom du chantier). */
  readonly contextLabel = input<string | null>(null);
  /** Clé i18n du titre d’intro (écran idle). */
  readonly infoTitleKey = input("mobile.voicePurchaseRequest.recorder.infoTitle");
  /** Clé i18n du texte d’intro (écran idle). */
  readonly infoBodyKey = input("mobile.voicePurchaseRequest.recorder.infoBody");

  /** Émis si le micro ou la transcription est indisponible. */
  readonly recordError = output<string>();
  /** Émis au clic sur Envoyer avec le texte final. */
  readonly sent = output<string>();

  protected readonly phase = signal<MobileVoicePurchaseRequestRecorderPhase>("idle");
  protected readonly elapsedSeconds = signal(0);
  protected readonly micDenied = signal(false);
  protected readonly transcript = signal("");
  protected readonly isEditing = signal(false);

  private readonly transcriptArea = viewChild<ElementRef<HTMLTextAreaElement>>("transcriptArea");

  private timerId: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      this.transcript();
      this.phase();
      this.isEditing();
      queueMicrotask(() => this.syncTranscriptAreaHeight());
    });
  }

  protected async onPrimaryAction(): Promise<void> {
    if (this.phase() === "recording") {
      await this.stopRecording();
      return;
    }
    if (this.phase() === "idle") {
      await this.startRecording();
    }
  }

  protected onModify(): void {
    this.isEditing.set(true);
    queueMicrotask(() => this.syncTranscriptAreaHeight());
  }

  protected onSend(): void {
    const text = this.transcript().trim();
    if (!text) {
      return;
    }
    this.sent.emit(text);
  }

  protected onTranscriptModelChange(value: string): void {
    this.transcript.set(value);
    if (this.phase() === "recording") {
      this.syncTranscriptAreaHeight();
    }
  }

  private syncTranscriptAreaHeight(): void {
    const el = this.transcriptArea()?.nativeElement;
    if (!el) {
      return;
    }

    const text = this.transcript();
    if (el.value !== text) {
      el.value = text;
    }

    if (this.phase() === "recording" || this.phase() === "finished") {
      el.style.height = "100%";
      el.style.overflowY = "auto";
      if (this.phase() === "recording") {
        el.scrollTop = el.scrollHeight;
      }
      return;
    }

    el.style.height = "auto";
    const contentHeight = el.scrollHeight;
    const maxHeightPx =
      typeof window !== "undefined" ? Math.min(window.innerHeight * 0.42, 300) : 300;
    const nextHeight = Math.min(Math.max(contentHeight, 64), maxHeightPx);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = contentHeight > maxHeightPx ? "auto" : "hidden";
  }

  ngOnDestroy(): void {
    this.voiceCapture.abortLiveTranscription();
    this.stopTimer();
  }

  private async startRecording(): Promise<void> {
    if (!this.voiceCapture.supportsLiveTranscription()) {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voicePurchaseRequest.recorder.error.speechUnsupported");
      return;
    }

    this.transcript.set("");
    this.isEditing.set(false);
    this.micDenied.set(false);

    const result = await this.voiceCapture.startLiveTranscription((text) => {
      this.transcript.set(text);
    });

    if (result === "unsupported") {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voicePurchaseRequest.recorder.error.speechUnsupported");
      return;
    }

    if (result === "mic-denied") {
      this.micDenied.set(true);
      this.recordError.emit("mobile.voiceReport.recorder.error.micDenied");
      return;
    }

    this.phase.set("recording");
    this.elapsedSeconds.set(0);
    this.startTimer();
  }

  private async stopRecording(): Promise<void> {
    const text = (await this.voiceCapture.stopLiveTranscription()).trim();
    this.stopTimer();
    this.phase.set("finished");
    this.transcript.set(text);
    queueMicrotask(() => this.syncTranscriptAreaHeight());
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = setInterval(() => {
      this.elapsedSeconds.update((elapsedSeconds) => elapsedSeconds + 1);
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerId != null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  protected formatElapsed(): string {
    const total = this.elapsedSeconds();
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  protected primaryLabelKey(): string {
    if (this.phase() === "recording") {
      return "mobile.voiceReport.recorder.finish";
    }
    return "mobile.voiceReport.recorder.start";
  }

  protected primaryIcon(): string {
    if (this.phase() === "recording") {
      return "stop_circle";
    }
    return "mic";
  }

  protected showTranscriptBlock(): boolean {
    return this.phase() === "recording" || this.phase() === "finished";
  }
}
